// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { HTMLInputTypeAttribute } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Box, TextField, TextFieldProps, Typography } from '@mui/material';
import useStyles from './styles';

interface TextInputProps extends TextFieldProps<'standard'> {
  label: string;
  value: string;
  enableTopLabel?: boolean;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  type?: HTMLInputTypeAttribute;
  validationMessage?: string;
  placeholder?: string;
  setValidationMessage?: React.Dispatch<React.SetStateAction<string>>;
  getValidationMessage?: (value: string) => string;
  getValidationMessageAsync?: (value: string) => Promise<string>;
  caption?: string;
}
interface TextInputLabelProps {
  label: string;
  required?: boolean;
  endAdornment?: React.ReactNode;
}
export const TextInputLabel: React.FC<TextInputLabelProps> = ({
  required,
  label,
  endAdornment,
}) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography
        fontWeight="bold"
        variant="caption"
        color="secondary"
        sx={{ textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      {required && <Typography color="error">*</Typography>}
      {endAdornment}
    </Box>
  );
};

const TextInput = ({
  label,
  value,
  setValue,
  type,
  validationMessage,
  setValidationMessage,
  getValidationMessage,
  getValidationMessageAsync,
  required,
  disabled,
  hidden,
  enableTopLabel,
  placeholder,
  caption,
}: TextInputProps) => {
  const { classes } = useStyles();
  const { showBoundary } = useErrorBoundary<Error>();

  if (hidden) {
    return null;
  }

  return (
    <>
      {enableTopLabel && <TextInputLabel required={required} label={label} />}
      {caption && (
        <Typography variant="caption" color="textSecondary">
          {caption}
        </Typography>
      )}

      <TextField
        size="small"
        variant="outlined"
        required={required}
        error={!!validationMessage}
        InputLabelProps={{ className: classes.inputFieldLabel }}
        className={classes.inputField}
        fullWidth
        placeholder={placeholder || label}
        type={type}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          if (setValidationMessage && getValidationMessage) {
            setValidationMessage(getValidationMessage(e.target.value));
          }
          if (setValidationMessage && getValidationMessageAsync) {
            getValidationMessageAsync(e.target.value)
              .then((x) => setValidationMessage(x ?? ''))
              .catch((error: Error) => showBoundary(error));
          }
        }}
        FormHelperTextProps={{ className: classes.inputValidationMessage }}
        helperText={validationMessage}
        disabled={disabled}
      />
      {enableTopLabel && <Box sx={{ pb: 2 }} />}
    </>
  );
};

export default TextInput;
